import {
  CloseCode,
  SUBPROTOCOL,
  TOKEN_SUBPROTOCOL_PREFIX,
  type Feed,
  type FeedClass,
  type RtDelta,
  type RtHello,
  type RtPrices,
  type RtSessionReset,
  type RtSnapshotBody,
  type ServerMessage,
  type Tier,
} from 'constants/payloads/realtime';
import { REALTIME_HIDDEN_CLOSE_ENABLED } from 'constants/flags';
import { getIsNativePlatform } from 'utils/client/mobile';
import { feedRequiresSnapshotDecompression, shouldAllowAnonymousRealtimeFallback } from './access';

export type RealtimeStatus = 'connecting' | 'live' | 'fallback' | 'off';

export interface RealtimeClientOptions {
  feed: Feed;
  /** REALTIME_URL base, e.g. wss://cf.insiderfinance.io/v1/realtime */
  baseUrl: string;
  params?: { tickers?: string[]; symbols?: string[]; feedClass?: FeedClass };
  /** Returns a Firebase ID token, or null/undefined for anonymous. `force` bypasses the token cache (4401 retry). */
  getToken: (force?: boolean) => Promise<string | null | undefined>;
  /** True when the viewer IS logged in — a token-less connect must then be treated as a
   * failure (not an anonymous downgrade), even on the very first attempt (§17.12 R5). */
  expectAuth?: boolean;
  onHello?: (hello: RtHello) => void;
  /** Full snapshot body (flow/darkpool RtSnapshotBody, or {quotes} for prices). */
  onSnapshot?: (body: unknown, hello: RtHello | null) => void;
  onDelta?: (delta: RtDelta) => void;
  onReset?: (reset: RtSessionReset) => void;
  onPrices?: (p: RtPrices) => void;
  onStatus?: (status: RealtimeStatus) => void;
  onTier?: (tier: Tier) => void;
}

// Liveness. A socket killed without a close frame (wifi flap, NAT/proxy idle-reap, laptop
// sleep) still reads as OPEN, so inbound traffic is the ONLY proof it is alive — and when
// the feed is quiet we have to manufacture that proof. These two bound how long a dead
// socket renders as live: SILENCE_MS + PONG_TIMEOUT_MS. At the original 90 s + 10 s that was
// ~100 s of frozen rows before we even started reconnecting, which is what "no trades came in
// for a couple of minutes" actually was. 20 s + 5 s puts the worst case at ~25 s.
//
// Cheap by construction: the DO registers a WebSocketRequestResponsePair for exactly these
// bytes, so Cloudflare's runtime answers the pong at the edge without waking the DO — no
// hibernation exit, no duration billing. (The inbound text ping is still metered as a
// WebSocket message; that is a fraction of a request each, not free, just negligible.) The
// 90 s was politeness toward a cost that barely exists.
//
// Silence-triggered, not a fixed interval: `armSilenceTimer` re-arms on every inbound frame,
// so an actively streaming socket never pings at all (its own traffic is the liveness proof)
// while the worst-case detection bound is identical either way.
//
// NOTE: a pong proves the edge socket is alive, NOT that trades are flowing — a stalled
// producer answers pongs happily. Pipeline liveness is covered by server-side monitoring.
const SILENCE_MS = 20_000; // send a ping after this much quiet (D18)
const PONG_TIMEOUT_MS = 5_000; // reconnect if no pong within this
const HIDDEN_CLOSE_MS = 60_000; // close after tab hidden this long (D24, flag-gated)
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_CAP_MS = 30_000;
const FALLBACK_COOLDOWN_MS = 10 * 60_000;
const ERROR_WINDOW_MS = 5 * 60_000;
const TOKEN_TIMEOUT_MS = 10_000; // bound a hung getToken() (§17.10 C15)

/**
 * Framework-free realtime feed client (spec §11.2). Owns connection lifecycle,
 * gzip-chunked snapshot reassembly, resume cursor, jittered reconnect,
 * liveness pings, tab-visibility close/resume, and the polling fallback signal.
 *
 * The sync mechanism is cursor discipline — no id-dedupe anywhere. Every row
 * carries one global seq and every frame is stamped with one; the client
 * buffers deltas during a snapshot and applies only those past the snapshot's
 * seq. See `handleServerMessage`.
 */
export class RealtimeClient {
  private readonly opts: RealtimeClientOptions;
  private ws: WebSocket | null = null;
  private stopped = false;
  // A fallback that retrying the SAME client (same params/auth) can never fix — a capability
  // gap (no DecompressionStream, §17.11.2) or a "do not retry with same params" close code
  // (4400/4403/4429/second-4401, §17.12.2). The retry timers skip re-arm for these; a genuine
  // fix (login, ticker change) recreates the client, clearing the flag.
  private permanentFallback = false;
  private status: RealtimeStatus = 'off';

  // Resume cursor (flow/darkpool). Prices never resumes.
  private sessionDate: string | null = null;
  private cursorSeq = 0;
  private hasCursor = false;
  private cursorTier: Tier | null = null; // tier the cursor was earned under (§17.11.2 keyhole guard)

  // Snapshot assembly.
  private assembling = false;
  private awaitingHello = false; // buffer deltas that (defensively) arrive before hello (§17.1 L1)
  private snapshotSeq = 0;
  private snapshotParts = 0;
  private binaryChunks: ArrayBuffer[] = [];
  private bufferedDeltas: RtDelta[] = [];
  private lastHello: RtHello | null = null;

  // Reconnect / fallback bookkeeping.
  private reconnectAttempts = 0;
  private consecutiveConnectFailures = 0;
  private reachedLive = false;
  private connecting = false; // guards against overlapping connect() attempts
  private forceTokenRefresh = false; // next connect bypasses the token cache (after 4401)
  private lastConnectHadToken = false; // a token-fetch failure after this shouldn't downgrade to anon (§17.8 C10)
  private suppressAuth = false; // optional-auth feeds can drop a rejected token and continue anon
  private connectSymbolsKey = ''; // prices symbol set the current socket's URL carried
  private errorTimestamps: number[] = [];
  private tokenRefreshedForThisConnect = false;

  // Timers.
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  private hiddenTimer: ReturnType<typeof setTimeout> | null = null;
  private visibilityHandler: (() => void) | null = null;

  constructor(opts: RealtimeClientOptions) {
    this.opts = opts;
  }

  start(): void {
    if (typeof window === 'undefined' || typeof WebSocket === 'undefined') return;
    this.stopped = false;
    // Gzip is required to inflate every snapshot. Safari/iOS ≤ 16.3 has no
    // DecompressionStream, so each snapshot would fail and the corrupt-snapshot path
    // would loop at ~1 s, re-downloading a full snapshot per cycle forever. Detect it
    // up front and hand off to polling — a capability gap, so no cooldown (§17.7 CL3).
    if (
      feedRequiresSnapshotDecompression(this.opts.feed) &&
      typeof DecompressionStream === 'undefined'
    ) {
      // A capability gap, not a transient blip — it will NEVER succeed on this browser, so
      // mark it permanent and the surface/context retry timers won't recreate a client every
      // 11 min for the tab's life (§17.11.2).
      this.permanentFallback = true;
      this.triggerFallback(false);
      return;
    }
    if (this.inCooldown()) {
      this.setStatus('fallback');
      return;
    }
    this.attachVisibility();
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.clearAllTimers();
    this.detachVisibility();
    this.closeSocket(1000, 'client stop');
    this.setStatus('off');
  }

  /** True when this client fell back due to a browser capability gap (no gzip), which no
   * amount of retrying will fix — retry timers should not recreate a client (§17.11.2). */
  isPermanentFallback(): boolean {
    return this.permanentFallback;
  }

  /** Send a client message (prices sub/unsub only). */
  send(msg: unknown): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  /**
   * Replace the prices symbol set without reconnecting (spec §6.3): sends `sub`
   * when the socket is open, and updates the params so any reconnect resumes the
   * new set. `sub` replaces the whole set; the server snapshots newly-added ones.
   */
  updateSymbols(symbols: string[]): void {
    if (this.opts.params) this.opts.params.symbols = symbols;
    this.send({ t: 'sub', symbols });
  }

  getStatus(): RealtimeStatus {
    return this.status;
  }

  // ── Connection ─────────────────────────────────────────────────────────────

  private async connect(): Promise<void> {
    // A pending reconnect is now happening — cancel its timer so it can't also fire.
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    // Never run two connects at once: a visibility flap during backoff, or two
    // `visible` events during the async getToken, would otherwise open two sockets
    // sharing one assembly buffer → interleaved gzip chunks → a corrupt-snapshot
    // loop that never counts as a failure.
    if (this.stopped || this.ws || this.connecting) return;
    this.connecting = true;
    this.setStatus('connecting');
    this.reachedLive = false;
    this.resetAssembly();

    let token: string | null | undefined = null;
    let tokenFailed = false;
    // Bound getToken: a hung (never-rejecting) Firebase fetch — e.g. after a hidden-tab
    // close — would otherwise leave the connect parked forever with frozen rows rendered
    // as live and polling disabled (§17.10 C15).
    const withTimeout = <T>(p: Promise<T>): Promise<T> =>
      Promise.race([
        p,
        new Promise<never>((_resolve, reject) =>
          setTimeout(() => reject(new Error('getToken timeout')), TOKEN_TIMEOUT_MS),
        ),
      ]);
    if (!this.suppressAuth) {
      try {
        token = await withTimeout(Promise.resolve(this.opts.getToken(this.forceTokenRefresh)));
      } catch {
        // Retry once with a forced refresh before giving up (§17.8 C10).
        try {
          token = await withTimeout(Promise.resolve(this.opts.getToken(true)));
        } catch {
          tokenFailed = true;
          token = null;
        }
      }
    }
    this.forceTokenRefresh = false;
    if (this.stopped) {
      this.connecting = false;
      return;
    }
    // Never silently downgrade an authenticated viewer to an anonymous (free-tier) socket: the
    // server grants free and no 4401 ever arrives to re-attempt the token, so a paid user would
    // stream 30-min-delayed free data rendered as live.
    if (this.opts.expectAuth === true && !token) {
      // Viewer is logged in but we have no token (a first-connect getToken failure/timeout, e.g.
      // securetoken.googleapis.com unreachable while the WS host is fine, §17.12 R5). Fall straight
      // to polling (correct data) WITHOUT an origin cooldown — two transient token blips must not
      // poison rt-cooldown for 10 min past a successful login; the 11-min retry re-attempts with a
      // fresh token, correct for both bad-token and transient causes (mirrors 4401, §17.13 S6c).
      this.connecting = false;
      this.triggerFallback(false);
      return;
    }
    if (
      tokenFailed &&
      this.lastConnectHadToken &&
      !shouldAllowAnonymousRealtimeFallback(this.opts.feed, this.opts.params?.feedClass)
    ) {
      // This client streamed authenticated before — a repeated token failure must not downgrade
      // to anon. Route through backoff/fallback so the token is retried (§17.8 C10).
      this.connecting = false;
      this.onConnectFailed();
      return;
    }
    this.lastConnectHadToken = !!token;

    const protocols = token ? [SUBPROTOCOL, TOKEN_SUBPROTOCOL_PREFIX + token] : [SUBPROTOCOL];
    this.connectSymbolsKey = this.symbolsKey();
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.buildUrl(), protocols);
    } catch {
      this.connecting = false;
      this.onConnectFailed();
      return;
    }
    ws.binaryType = 'arraybuffer';
    this.ws = ws;

    ws.onopen = () => {
      this.connecting = false;
      this.reconnectAttempts = 0;
      this.armSilenceTimer();
      // If a backoff reconnect fired while the tab is backgrounded, this socket opened
      // hidden with no close timer armed (the visibility handler only arms on the
      // hidden EVENT) — arm it now so a hidden tab can't hold a socket open forever
      // (D24 regression, §17.6 C6).
      if (typeof document !== 'undefined' && document.hidden) {
        this.armHiddenTimer();
      }
      // Prices: if the desired symbol set changed during the handshake (a `sub`
      // sent while CONNECTING was a no-op), sync it now so a mid-handshake
      // registration actually streams (§17.2 P2).
      if (
        this.opts.feed === 'prices' &&
        this.opts.params?.symbols &&
        this.symbolsKey() !== this.connectSymbolsKey
      ) {
        this.send({ t: 'sub', symbols: this.opts.params.symbols });
      }
    };
    ws.onmessage = (ev: MessageEvent) => this.handleMessage(ev);
    ws.onerror = () => this.recordError();
    ws.onclose = (ev: CloseEvent) => this.handleClose(ev.code, ev.reason);
  }

  private symbolsKey(): string {
    return (this.opts.params?.symbols ?? []).slice().sort().join(',');
  }

  private buildUrl(): string {
    const base = this.opts.baseUrl.replace(/\/$/, '');
    const params = new URLSearchParams();
    if (this.opts.params?.tickers?.length)
      params.set('tickers', this.opts.params.tickers.join(','));
    if (this.opts.params?.symbols?.length)
      params.set('symbols', this.opts.params.symbols.join(','));
    if (this.opts.params?.feedClass) params.set('feedClass', this.opts.params.feedClass);
    if (this.hasCursor && this.sessionDate)
      params.set('since', `${this.sessionDate}:${this.cursorSeq}`);
    const qs = params.toString();
    return `${base}/${this.opts.feed}${qs ? `?${qs}` : ''}`;
  }

  // ── Message handling ────────────────────────────────────────────────────────

  private handleMessage(ev: MessageEvent): void {
    this.armSilenceTimer();
    // ANY inbound frame proves the socket is receiving — that is the whole property the pong
    // stands in for, so a delta (or a binary snapshot chunk) arriving inside the grace period
    // must satisfy an outstanding deadline too. Clearing only on `pong` meant a feed that woke
    // up mid-probe got its healthy socket torn down. Latent before, but the 20 s keepalive
    // pings often enough during pre-market/lunch lulls to actually hit it.
    this.clearPongTimer();
    if (typeof ev.data !== 'string') {
      // Binary snapshot chunk.
      const buf = ev.data instanceof ArrayBuffer ? ev.data : null;
      if (buf && this.assembling) this.binaryChunks.push(buf);
      return;
    }

    let msg: ServerMessage;
    try {
      msg = JSON.parse(ev.data) as ServerMessage;
    } catch {
      return;
    }
    this.handleServerMessage(msg);
  }

  private handleServerMessage(msg: ServerMessage): void {
    switch (msg.t) {
      case 'hello':
        this.lastHello = msg;
        this.opts.onHello?.(msg);
        this.opts.onTier?.(msg.tier);
        // Tier changed under a live cursor — a mid-session free→paid upgrade with no client
        // recreation (viewerId is unchanged). The server already resumed from the FREE-era
        // cursor under the now-PAID filter, silently skipping every withheld row ≤ cursor.
        // Discard this connection and reconnect fresh for a full snapshot under the new tier.
        if (this.hasCursor && this.cursorTier !== null && this.cursorTier !== msg.tier) {
          this.resetCursor();
          this.reconnect(true);
          return;
        }
        this.cursorTier = msg.tier; // this connection's cursor belongs to this tier (§17.11.2)
        // A resume:'full' means a snapshot follows — buffer any interleaved
        // deltas until snapshot_end. diff/none go straight to live.
        this.assembling = msg.resume === 'full';
        if (this.opts.feed === 'prices') {
          // prices sends hello then an inline snapshot; treat that as live.
          this.assembling = false;
        }
        this.awaitingHello = false;
        if (!this.assembling) {
          // diff/none: flush any pre-hello buffered deltas, then go live.
          this.flushPendingDeltas(this.cursorSeq);
          this.markLive();
        }
        break;

      case 'snapshot_begin':
        this.assembling = true;
        this.snapshotSeq = msg.seq;
        this.snapshotParts = msg.parts;
        this.binaryChunks = [];
        break;

      case 'snapshot_end':
        void this.finishSnapshot();
        break;

      case 'snapshot': // prices inline
        this.opts.onSnapshot?.({ quotes: msg.quotes }, this.lastHello);
        this.markLive();
        break;

      case 'delta':
        this.onDeltaFrame(msg);
        break;

      case 'session_reset':
        this.onSessionReset(msg);
        break;

      case 'prices':
        this.opts.onPrices?.(msg);
        break;

      case 'pong':
        // Deadline already cleared in handleMessage — every inbound frame counts, not just this one.
        break;

      case 'error':
        // Non-fatal protocol error; the close code drives fallback.
        break;
    }
  }

  private async finishSnapshot(): Promise<void> {
    // Capture the socket this snapshot belongs to BEFORE the async inflate. A
    // rollover-triggered reconnect(true) whose backoff completes during a slow multi-MB
    // gunzip can swap in a NEW socket — delivering this OLD-day body would clobber the new
    // handshake (assembling=false drops its chunks) and mint a bogus cursor (§17.11 N3).
    const ws = this.ws;
    const chunks = this.binaryChunks;
    this.binaryChunks = [];
    // Truncation guard: a short/over-long frame count means a dropped binary frame —
    // treat as corrupt. Route through onConnectFailed() (not a bare reconnect) so
    // repeated failures count toward fallback and back off, instead of looping at ~1 s
    // downloading a full snapshot per cycle forever (§17.7 CL3).
    if (chunks.length !== this.snapshotParts) {
      this.resetCursor();
      this.onConnectFailed();
      return;
    }
    try {
      const body = await this.inflate(chunks);
      // A ticker switch, or a rollover mid-gunzip that already reconnected and REPLACED
      // (or nulled) the socket, means this snapshot is for the OLD params/socket —
      // delivering it would leak the old ticker's rows into the new page's state or clobber
      // the new handshake. Identity check, not just null (§17.6 C3, §17.7 CL7, §17.11 N3).
      if (this.stopped || this.ws !== ws) return;
      this.opts.onSnapshot?.(body, this.lastHello);
    } catch {
      // Corrupt snapshot — count toward fallback + back off (§17.7 CL3). But if the socket
      // was already swapped during the inflate, this failure belongs to the OLD connection —
      // don't reset the cursor / strike the HEALTHY new one (same identity guard, §17.12.2).
      if (this.stopped || this.ws !== ws) return;
      this.resetCursor();
      this.onConnectFailed();
      return;
    }
    // Apply buffered deltas past the snapshot seq (§11.2 cursor discipline).
    this.sessionDate = this.lastHello?.sessionDate ?? this.sessionDate;
    this.cursorSeq = this.snapshotSeq;
    this.hasCursor = true;
    const pending = this.bufferedDeltas
      .filter((d) => d.seq > this.snapshotSeq)
      .sort((a, b) => a.seq - b.seq);
    this.bufferedDeltas = [];
    this.assembling = false;
    for (const d of pending) this.applyDelta(d);
    this.markLive();
  }

  private onDeltaFrame(delta: RtDelta): void {
    if (this.awaitingHello || this.assembling) {
      // A rollover mid-handshake restarts seqs at 1 — the seq>S rule would
      // misfire, so abort assembly and reconnect fresh with no cursor (§11.2).
      if (this.lastHello && delta.sessionDate !== this.lastHello.sessionDate) {
        this.resetCursor();
        this.reconnect(true);
        return;
      }
      this.bufferedDeltas.push(delta);
      return;
    }
    this.applyDelta(delta);
  }

  /** Apply buffered deltas with seq > minSeq, ascending, then clear the buffer. */
  private flushPendingDeltas(minSeq: number): void {
    const pending = this.bufferedDeltas.filter((d) => d.seq > minSeq).sort((a, b) => a.seq - b.seq);
    this.bufferedDeltas = [];
    for (const d of pending) this.applyDelta(d);
  }

  private applyDelta(delta: RtDelta): void {
    // Cursor-monotonic CLAMP (not a skip): never regress the cursor, but ALWAYS deliver the
    // rows. All four surface hooks merge by seq-keyed Map, so a re-delivered row is idempotent —
    // whereas SKIPPING an out-of-order frame (a pre-hello buffered live delta, an oversized-frame
    // split, a relay reorder) turns a self-healing duplicate into PERMANENT loss. Clamp keeps both
    // protections (no cursor regression, no spurious resume re-fetch) at none of the cost (§17.13 S5).
    const sameSession = this.hasCursor && delta.sessionDate === this.sessionDate;
    this.sessionDate = delta.sessionDate;
    this.cursorSeq = sameSession ? Math.max(this.cursorSeq, delta.seq) : delta.seq;
    this.hasCursor = true;
    this.opts.onDelta?.(delta);
  }

  private onSessionReset(reset: RtSessionReset): void {
    if (this.assembling) {
      // Rollover arrived while a snapshot was still streaming — clean reconnect.
      this.resetCursor();
      this.reconnect(true);
      return;
    }
    this.sessionDate = reset.sessionDate;
    this.cursorSeq = 0;
    this.hasCursor = false;
    this.opts.onReset?.(reset);
  }

  private async inflate(chunks: ArrayBuffer[]): Promise<RtSnapshotBody | unknown> {
    const total = chunks.reduce((n, c) => n + c.byteLength, 0);
    const merged = new Uint8Array(total);
    let offset = 0;
    for (const c of chunks) {
      merged.set(new Uint8Array(c), offset);
      offset += c.byteLength;
    }
    const stream = new Response(merged).body!.pipeThrough(new DecompressionStream('gzip'));
    const text = await new Response(stream).text();
    return JSON.parse(text);
  }

  // ── Liveness ────────────────────────────────────────────────────────────────

  private armSilenceTimer(): void {
    if (this.silenceTimer) clearTimeout(this.silenceTimer);
    this.silenceTimer = setTimeout(() => this.onSilence(), SILENCE_MS);
  }

  private onSilence(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send('{"t":"ping"}');
    this.clearPongTimer();
    this.pongTimer = setTimeout(() => this.reconnect(false), PONG_TIMEOUT_MS);
  }

  private clearPongTimer(): void {
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
  }

  // ── Visibility (D24) ─────────────────────────────────────────────────────────

  /**
   * Arm the hidden-tab close timer once. The callback nulls `hiddenTimer` BEFORE
   * closing so the `!this.hiddenTimer` re-arm guards (here and in `onopen`) fire again
   * on the next hide — the old code left a stale handle after the first fire, so a
   * later backoff reconnect while hidden held a socket open forever (§17.7 CL1).
   *
   * Gated OFF for web by REALTIME_HIDDEN_CLOSE_ENABLED: closing a backgrounded tab's socket
   * saves a server fan-out cost we do not measurably pay, and bills the user a token fetch +
   * handshake + possible full snapshot on return. Gating here rather than at the call sites
   * covers all three arm points (hide event, already-hidden attach, hidden `onopen`) and
   * leaves the clear-on-visible path intact for a flip back on.
   *
   * Native/Capacitor keeps the close regardless of the flag. D24's stated rationale was mobile
   * battery/data, and that half of it survives scrutiny: the cost that moved is on the CLIENT,
   * not the DO, and a backgrounded phone streaming the firehose over cellular pays it for a
   * screen nobody is looking at. Desktop tabs — the actual complaint — pay no such price.
   */
  private armHiddenTimer(): void {
    const nativeBuild = getIsNativePlatform() || process.env.TARGET_PLATFORM === 'MOBILE';
    if (!REALTIME_HIDDEN_CLOSE_ENABLED && !nativeBuild) return;
    if (this.hiddenTimer) return;
    this.hiddenTimer = setTimeout(() => {
      this.hiddenTimer = null;
      this.closeSocket(1000, 'tab hidden');
      // closeSocket detaches handlers, so set the status here (no close event fires). NOTE:
      // 'connecting' does NOT un-stick the surfaces — their `status === 'connecting' && rows`
      // guard keeps the last rows rendered as live through the hidden period + resume window.
      // That's intentional (no flash-to-empty on a brief hide→re-show); the rows are frozen
      // but recent and the resume on the next visibilitychange refreshes them (§17.8 C13).
      this.setStatus('connecting');
    }, HIDDEN_CLOSE_MS);
  }

  private attachVisibility(): void {
    if (typeof document === 'undefined' || this.visibilityHandler) return;
    this.visibilityHandler = () => {
      if (document.hidden) {
        this.armHiddenTimer();
      } else {
        if (this.hiddenTimer) {
          clearTimeout(this.hiddenTimer);
          this.hiddenTimer = null;
        }
        if (this.stopped) return;
        if (!this.ws || this.ws.readyState >= WebSocket.CLOSING) {
          void this.connect();
        } else if (this.ws.readyState === WebSocket.OPEN) {
          // Do NOT trust readyState. A socket killed while backgrounded — wifi flap, laptop
          // sleep, mobile radio teardown — still reports OPEN, and the old code took that at
          // face value and returned you to rows that were already dead. Probe immediately
          // instead of waiting out a full silence cycle: this is the path that decides whether
          // what you are looking at on return is live or up to 25 s stale.
          this.onSilence();
        }
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
    // If the tab is already hidden when we connect, arm the close timer now —
    // otherwise a background-opened tab holds a socket open forever (D24 gap).
    if (document.hidden) {
      this.armHiddenTimer();
    }
  }

  private detachVisibility(): void {
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    this.visibilityHandler = null;
  }

  // ── Close / reconnect / fallback ─────────────────────────────────────────────

  private handleClose(code: number, _reason: string): void {
    this.connecting = false;
    this.clearPongTimer();
    if (this.silenceTimer) clearTimeout(this.silenceTimer);
    this.ws = null;
    if (this.stopped) return;

    switch (code) {
      case CloseCode.BAD_REQUEST:
      case CloseCode.FORBIDDEN:
      case CloseCode.OVER_LIMIT:
        // Do not retry with the same params — hand off to polling WITHOUT writing an
        // origin-wide cooldown (these are per-params terminal; a different page/params
        // must not be poisoned by this one) (§17.6 C5). Terminal for THIS client's params, so
        // the retry timers must not reissue the identical doomed connect every 11 min (§17.12.2).
        this.permanentFallback = true;
        this.triggerFallback(false);
        return;
      case CloseCode.UNAUTHENTICATED:
        // Force a fresh token (the cached one is invalid/expired) once, then
        // reconnect; if it recurs, fall back.
        if (!this.tokenRefreshedForThisConnect) {
          this.tokenRefreshedForThisConnect = true;
          this.forceTokenRefresh = true;
          this.reconnect(false, 0);
        } else if (
          shouldAllowAnonymousRealtimeFallback(this.opts.feed, this.opts.params?.feedClass)
        ) {
          // Prices is public: auth is an upgrade path, not a gate. If the server rejects both
          // the cached and force-refreshed token, drop auth and keep the base stream alive.
          this.suppressAuth = true;
          this.forceTokenRefresh = false;
          this.tokenRefreshedForThisConnect = false;
          this.lastConnectHadToken = false;
          this.reconnect(false, 0);
        } else {
          // Auth-terminal — per-user, not a health issue. Don't write the origin-wide cooldown,
          // or a user who then logs in properly still gets 10 min of polling (§17.9.2). NOT
          // permanentFallback: 4401 is also emitted for server-transient verify failures (a
          // cold-isolate JWKS fetch, clock skew) that hit every user on a PoP — killing realtime
          // for the tab's lifetime would be an overreaction. The 11-min retry reissues with a
          // fresh forced token, correct for both bad-token and transient-verify causes (§17.13 S4).
          this.triggerFallback(false);
        }
        return;
      case CloseCode.REAUTH:
        // Expected 4 h reauth — silent reconnect + resume, not a failure.
        this.tokenRefreshedForThisConnect = false;
        this.reconnect(false, 0);
        return;
      default:
        // 1012 deploy, 1006 network, etc. — reconnect with jitter + resume.
        if (!this.reachedLive) this.onConnectFailed();
        else this.reconnect(false);
    }
  }

  /** The user is not looking at this surface right now. */
  private isHidden(): boolean {
    return typeof document !== 'undefined' && document.hidden === true;
  }

  /**
   * A hidden tab must not spend the terminal-fallback budget. With the hidden-close off it now
   * reconnects in the background, so a 30 s wifi blip while the user sits in another tab would
   * burn both attempts, write the 10-minute cooldown, and hand them polling for up to 11 minutes
   * on a surface they were never watching — strictly worse than the old behavior, which simply
   * waited for them to come back. Keep retrying under backoff (capped at 30 s, harmless) and let
   * the failure count escalate the moment they are actually looking. The budget is NOT reset on
   * return: if it still fails with the tab open, falling back promptly is the right answer.
   *
   * Terminal close codes (4400/4403/4429, capability gaps) never route through here — they set
   * `permanentFallback` directly — so this cannot keep a doomed connect alive.
   */
  private onConnectFailed(): void {
    this.consecutiveConnectFailures++;
    if (this.consecutiveConnectFailures >= 2 && !this.isHidden()) {
      this.triggerFallback();
      return;
    }
    this.reconnect(false);
  }

  private recordError(): void {
    const now = Date.now();
    this.errorTimestamps = this.errorTimestamps.filter((t) => now - t < ERROR_WINDOW_MS);
    this.errorTimestamps.push(now);
    if (this.errorTimestamps.length >= 3 && !this.isHidden()) this.triggerFallback();
  }

  private reconnect(fresh: boolean, delayOverride?: number): void {
    if (this.stopped) return;
    this.closeSocket(1000, 'reconnect');
    if (fresh) this.resetCursor();
    const delay = delayOverride ?? this.nextBackoff();
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => void this.connect(), delay);
  }

  private nextBackoff(): number {
    const raw = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** this.reconnectAttempts);
    this.reconnectAttempts++;
    const jitter = 0.5 + Math.random(); // ±50%
    return Math.round(raw * jitter);
  }

  /**
   * Hand off to polling — terminal for this client instance. Visibility flaps and
   * backoff timers must NOT reconnect after this (they'd retry do-not-retry codes
   * forever), so we stop and detach visibility; the hook recreates the client on a
   * genuine param/auth change (§17.6 C1). `writeCooldown` is false for per-params
   * terminal codes (4400/4403/4429) so one bad ticker page can't poison main-flow
   * realtime origin-wide (§17.6 C5) — only network/health failures cool down.
   */
  private triggerFallback(writeCooldown = true): void {
    this.stopped = true;
    this.setStatus('fallback');
    if (writeCooldown) {
      try {
        window.localStorage.setItem(
          `rt-cooldown:${this.opts.feed}`,
          String(Date.now() + FALLBACK_COOLDOWN_MS),
        );
      } catch {
        /* private mode / no storage */
      }
    }
    this.clearAllTimers();
    this.detachVisibility();
    this.closeSocket(1000, 'fallback');
  }

  private inCooldown(): boolean {
    try {
      const until = parseInt(
        window.localStorage.getItem(`rt-cooldown:${this.opts.feed}`) || '0',
        10,
      );
      return Number.isFinite(until) && Date.now() < until;
    } catch {
      return false;
    }
  }

  private markLive(): void {
    this.reachedLive = true;
    this.consecutiveConnectFailures = 0;
    this.tokenRefreshedForThisConnect = false;
    // Reaching live clears the error window — else three transient blips spread across
    // an otherwise-healthy 5 min still trip the terminal fallback + cooldown (§17.9.2).
    this.errorTimestamps = [];
    this.setStatus('live');
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────

  private setStatus(status: RealtimeStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.opts.onStatus?.(status);
  }

  private resetAssembly(): void {
    this.assembling = false;
    this.awaitingHello = true; // until hello, buffer any delta (defensive, §17.1 L1)
    this.binaryChunks = [];
    this.bufferedDeltas = [];
    this.snapshotSeq = 0;
    this.snapshotParts = 0;
  }

  private resetCursor(): void {
    this.sessionDate = null;
    this.cursorSeq = 0;
    this.hasCursor = false;
    this.cursorTier = null;
  }

  private closeSocket(code: number, reason: string): void {
    const ws = this.ws;
    this.ws = null;
    // Only clear the connect guard when there was actually a socket to tear down. If
    // closeSocket runs with no socket (the hidden-close firing while a connect is still
    // parked on getToken()), clearing it would let a visibility-show start a SECOND
    // connect → two sockets sharing one assembly buffer, the C2 corruption loop. An
    // in-flight connect clears its own guard via onopen / its stopped check (§17.7 CL2).
    if (!ws) return;
    // Tear-down on the error→fallback path detaches onclose (so handleClose never runs
    // to reset it), so clear the guard here or every later connect() bails (§17.6 C4).
    this.connecting = false;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    try {
      ws.close(code, reason);
    } catch {
      /* already closing */
    }
  }

  private clearAllTimers(): void {
    for (const t of [this.reconnectTimer, this.silenceTimer, this.pongTimer, this.hiddenTimer]) {
      if (t) clearTimeout(t);
    }
    this.reconnectTimer = null;
    this.silenceTimer = null;
    this.pongTimer = null;
    this.hiddenTimer = null;
  }
}
